import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Check, ChevronsUpDown } from 'lucide-react';

/**
 * Lightweight combobox: typing-driven autocomplete that ALSO accepts free-text
 * for cities not in the list (foreign passports, niche PSKs, etc.).
 *
 * Used for "Passport Issued At" — the underlying form value is always a string,
 * so this is a drop-in replacement for a controlled <Input> or <Select>.
 *
 * Props:
 *   value         — current string
 *   onChange(str) — new value, fires on every keystroke + on suggestion click
 *   options       — array of strings (suggestions)
 *   placeholder   — optional placeholder
 *   testId        — applied to the visible input
 *   className     — wrapper class (defaults to standard h-12 rounded-xl)
 *   maxResults    — max suggestions to show (default 8)
 */
export default function CityCombobox({
  value = '',
  onChange,
  options = [],
  placeholder = 'Type city…',
  testId,
  className = 'h-12 rounded-xl',
  maxResults = 8,
  inputProps = {},
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapperRef = useRef(null);

  const suggestions = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (!q) return options.slice(0, maxResults);
    return options
      .filter((c) => c.toLowerCase().includes(q))
      .sort((a, b) => {
        const ai = a.toLowerCase().indexOf(q);
        const bi = b.toLowerCase().indexOf(q);
        return ai - bi || a.localeCompare(b);
      })
      .slice(0, maxResults);
  }, [value, options, maxResults]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const choose = (s) => {
    onChange?.(s);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId ? `${testId}-wrapper` : undefined}>
      <Input
        {...inputProps}
        value={value}
        onChange={(e) => { onChange?.(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(suggestions.length - 1, a + 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
          else if (e.key === 'Enter' && suggestions[active]) { e.preventDefault(); choose(suggestions[active]); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        placeholder={placeholder}
        className={`pr-10 ${className}`}
        data-testid={testId}
      />
      <ChevronsUpDown
        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 cursor-pointer"
        onClick={() => setOpen((o) => !o)}
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto py-1"
          data-testid={testId ? `${testId}-options` : undefined}
        >
          {suggestions.map((s, i) => (
            <li
              key={s}
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              className={`px-4 py-2 text-sm cursor-pointer flex items-center gap-2 ${i === active ? 'bg-teal-50 text-teal-700' : 'hover:bg-slate-50 text-slate-700'}`}
            >
              {value && s.toLowerCase() === value.toLowerCase() && <Check className="w-3.5 h-3.5 text-teal-600" />}
              {s}
            </li>
          ))}
          {value && !suggestions.some((s) => s.toLowerCase() === value.toLowerCase()) && (
            <li className="px-4 py-1.5 text-[10px] text-slate-400 italic border-t border-slate-100">
              Press Enter to use “{value}” as a custom value
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
